/** Customer-facing marketing copy only — no technical / API details. */

export type MarketingBlock = {
  eyebrow: string;
  title: string;
  summary: string;
  bullets: string[];
  ctaLabel?: string;
  ctaHref?: string;
};

export const MARKETING_BY_SLUG: Record<string, MarketingBlock> = {
  web: {
    eyebrow: "Web",
    title: "Web that wins customers",
    summary:
      "Launch fast, look professional, and scale when you grow. We host, secure, and support your online presence so you can focus on sales.",
    bullets: [
      "Modern sites that load quickly on phone and desktop",
      "Managed hosting with monitoring and backups",
      "Expert help when you need changes or campaigns",
    ],
    ctaLabel: "Explore servers",
    ctaHref: "/web/servers",
  },
  "web/servers": {
    eyebrow: "Web · Servers",
    title: "Select and buy your cloud server",
    summary:
      "Pick a Website Server, tell us who you are, and we launch a dedicated AWS t2-class instance for your site — learned from our EC2 admin console, built for customers here.",
    bullets: [
      "Choose Website Server from the dropdown",
      "Dedicated starter instance — not shared crowded hosting",
      "$20 refundable deposit toward provisioning",
    ],
  },
  "web/services": {
    eyebrow: "Web · Services",
    title: "Done-for-you web services",
    summary:
      "From design refreshes to integrations and ongoing care plans, we keep your site healthy and your brand sharp.",
    bullets: [
      "Design, content updates, and performance tuning",
      "E-commerce and payment integrations",
      "Monthly care plans with clear SLAs",
    ],
    ctaLabel: "Get started",
    ctaHref: "#contact",
  },
  pos: {
    eyebrow: "POS",
    title: "AI4POS — point of sale, enhanced by AI",
    summary:
      "Backed by 30+ years in retail POS. AI4POS brings proven checkout, reporting, and inventory intelligence together in one modern system.",
    bullets: [
      "Core POS features retailers rely on every day",
      "AI inventory forecasting and purchase-order optimization",
      "E-commerce ready — sell in store and online",
      "Free 30-day trial to see it in your business",
    ],
    ctaLabel: "Visit AI4POS",
    ctaHref: "https://ai4pos.com/marketing/",
  },
  cart: {
    eyebrow: "Cart",
    title: "Shopping cart that converts",
    summary:
      "Smooth checkout, fewer abandoned carts, and clear order tracking for your customers.",
    bullets: [
      "Fast, mobile-friendly checkout",
      "Works with your POS and web catalog",
      "Promotions and loyalty-ready",
    ],
    ctaLabel: "Talk to sales",
    ctaHref: "#contact",
  },
  "ai-models": {
    eyebrow: "AI Models",
    title: "AI Models for Business Agility & Transformation",
    summary:
      "Turn business data into actionable intelligence with pre-built AI models designed to solve real-world business challenges. From inventory forecasting and sales projections to vendor minimum purchase recommendations and website traffic risk analysis, our AI helps businesses anticipate challenges, optimize operations, and make smarter decisions.",
    bullets: [
      "Inventory Forecasting: Predict inventory demand, identify potential shortages, and optimize stock levels.",
      "Sales Projections: Forecast sales trends, identify patterns, and support better business planning.",
      "Vendor Purchase Recommendations: Analyze minimum order quantities and recommend smarter purchasing decisions.",
      "Website Traffic Risk Analysis: Detect unusual traffic patterns, identify potential threats, and assess website security risks.",
      "AI-Powered Recommendations: Translate complex model outputs into clear, practical recommendations that teams can understand and act on.",
      "Custom AI Training: Optionally train models using your own business data for insights tailored to your operations.",
    ],
    ctaLabel: "Train AI models",
    ctaHref: "/ai-models/train",
  },
  "ai-models/train": {
    eyebrow: "AI Models",
    title: "Convert Your Data into AI Models",
    summary:
      "Upload a CSV and train with the train_model2-1 engine used by our customer portal.",
    bullets: [
      "Upload inventory or sales history",
      "Pick a numeric target column",
      "Review holdout metrics before you use the model",
    ],
    ctaLabel: "Schedule a Consultation",
    ctaHref: "/ai-models/train",
  },
  "ai-agents": {
    eyebrow: "AI Agents",
    title: "AI Agents to answer questions about resumes and leases",
    summary:
      "Upload a resume, lease, or spreadsheet and ask what matters — search, summarize, and analyze without digging through pages yourself.",
    bullets: [
      "Answers you can share with hiring or leasing teams",
      "Works with Word, Excel, and PDF uploads",
      "Ask follow-up questions in the same session",
    ],
    ctaLabel: "Train AI Agent",
    ctaHref: "/ai-agents/ask",
  },
  "ai-agents/ask": {
    eyebrow: "AI Agents",
    title: "Make Agent Analyze, Search your files",
    summary:
      "Upload a Word, Excel, or PDF file and ask questions about it in plain English.",
    bullets: [
      "Choose leases or resumes category",
      "Ask what is in the document",
      "Iterate with more questions in the same session",
    ],
    ctaLabel: "Schedule a Consultation",
    ctaHref: "/ai-agents/ask",
  },
  "trading-engine": {
    eyebrow: "Trading Engine",
    title: "Trading engine for disciplined execution",
    summary:
      "Rules-based strategies, risk limits, and clear signals — built for teams that take execution seriously.",
    bullets: [
      "Strategy templates and custom logic",
      "Risk caps and position sizing",
      "Audit-friendly history",
    ],
    ctaLabel: "demo.tnktrade.ai",
    ctaHref: "https://demo.tnktrade.ai/login",
  },
  "doc-mngt": {
    eyebrow: "Doc Mngt",
    title: "Document management, simplified",
    summary:
      "Store, find, and share business documents with permissions your team understands.",
    bullets: [
      "Central library with search",
      "Version history and approvals",
      "Works with your existing tools",
    ],
    ctaLabel: "Learn more",
    ctaHref:
      "https://www.delaportal.com/delaportal/demo/delaportal/demo.cfm?company=none&lang=en",
  },
};

export function marketingForSlug(slug: string): MarketingBlock | null {
  return MARKETING_BY_SLUG[slug] ?? null;
}
